import { useEffect, useState, useMemo } from 'react';
import type { MouseEvent } from 'react';
import { Editor } from '@monaco-editor/react';
import { githubFileGet } from '../../api/github/fileGet';
import { githubTreeGet } from '../../api/github/treeGet';
import { githubTreeSimplify } from '../../api/github/treeSimplify';
import { vscodeTheme } from '../../styles/theme';
import { useToast } from '../shared/Toast';
import { getLanguageFromPath, isBinaryExtension } from '../../utils/fileLanguage';

interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  children?: TreeNode[];
}

interface GitHubRepositorySelection {
  owner: string;
  repo: string;
  branch: string;
}

interface GitHubFileTreeProps {
  repository: GitHubRepositorySelection;
  selectedFile: string | null;
  onFileSelect: (path: string) => void;
}

const normalizeDirectoryPath = (path: string) =>
  path.endsWith('/') ? path : `${path}/`;

const sortTreeNodes = (nodes: TreeNode[]) => {
  nodes.sort((a, b) => {
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    return a.name.localeCompare(b.name);
  });
  for (const node of nodes) {
    if (node.children) {
      sortTreeNodes(node.children);
    }
  }
};

const buildTreeFromFlatList = (items: Array<{ path: string; type: 'blob' | 'tree' }>) => {
  const root: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  const ensureFolder = (folderPath: string) => {
    const normalized = normalizeDirectoryPath(folderPath);
    const trimmed = normalized.replace(/\/$/, '');
    if (!trimmed) {
      return null;
    }
    const parts = trimmed.split('/').filter(Boolean);
    let currentPath = '';
    let parent = root;
    let currentNode: TreeNode | null = null;

    for (const part of parts) {
      currentPath += `${part}/`;
      const existing = nodeMap.get(currentPath);
      if (existing) {
        currentNode = existing;
      } else {
        currentNode = {
          name: part,
          path: currentPath,
          isFolder: true,
          children: [],
        };
        nodeMap.set(currentPath, currentNode);
        parent.push(currentNode);
      }
      currentNode.children = currentNode.children ?? [];
      parent = currentNode.children;
    }

    return currentNode;
  };

  const addFileNode = (filePath: string) => {
    if (nodeMap.has(filePath)) {
      return;
    }
    const trimmed = filePath.replace(/\/$/, '');
    const parts = trimmed.split('/').filter(Boolean);
    const fileName = parts.pop();
    if (!fileName) {
      return;
    }
    const parentPath = parts.length ? `${parts.join('/')}/` : '';
    const parentNode = parentPath ? ensureFolder(parentPath) : null;
    const fileNode: TreeNode = {
      name: fileName,
      path: filePath,
      isFolder: false,
    };
    nodeMap.set(filePath, fileNode);
    if (parentNode) {
      parentNode.children = parentNode.children ?? [];
      parentNode.children.push(fileNode);
    } else {
      root.push(fileNode);
    }
  };

  for (const item of items) {
    if (item.type === 'tree') {
      ensureFolder(item.path);
    } else {
      addFileNode(item.path);
    }
  }

  sortTreeNodes(root);

  return root;
};

export function GitHubFileTree({
  repository,
  selectedFile,
  onFileSelect,
}: GitHubFileTreeProps) {
  const { showToast } = useToast();
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [copying, setCopying] = useState(false);
  const [copyingPaths, setCopyingPaths] = useState<Set<string>>(new Set());

  // Merge feature state
  const [selectedFilePaths, setSelectedFilePaths] = useState<Set<string>>(new Set());
  const [isMerging, setIsMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState({ current: 0, total: 0 });
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergedContent, setMergedContent] = useState('');

  useEffect(() => {
    const loadTree = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await githubTreeGet({
          owner: repository.owner,
          repo: repository.repo,
          ref: repository.branch,
          recursive: true,
        });
        const rootTree = buildTreeFromFlatList(data.tree);
        setTree(rootTree);
        const newExpanded = new Set<string>();
        const walk = (nodes: TreeNode[]) => {
          for (const node of nodes) {
            if (node.isFolder) {
              newExpanded.add(node.path);
              if (node.children) {
                walk(node.children);
              }
            }
          }
        };
        walk(rootTree);
        setExpandedFolders(newExpanded);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load files');
        showToast('GitHubファイルの取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    };

    void loadTree();
  }, [repository.branch, repository.owner, repository.repo, showToast]);

  // Reset selection when repository changes
  useEffect(() => {
    setSelectedFilePaths(new Set());
  }, [repository.owner, repository.repo, repository.branch]);

  // Constants for merge feature
  const MAX_SELECTED_FILES = 100;
  const PARALLEL_FETCH_LIMIT = 10;

  // Helper: Get all file paths from tree (recursive)
  const getAllFilePaths = (nodes: TreeNode[]): string[] => {
    const paths: string[] = [];
    const walk = (items: TreeNode[]) => {
      for (const item of items) {
        if (item.isFolder) {
          if (item.children) {
            walk(item.children);
          }
        } else {
          paths.push(item.path);
        }
      }
    };
    walk(nodes);
    return paths;
  };

  // Helper: Get all file paths under a specific node
  const getDescendantFilePaths = (node: TreeNode): string[] => {
    if (!node.isFolder) {
      return [node.path];
    }
    const paths: string[] = [];
    const walk = (n: TreeNode) => {
      if (n.isFolder) {
        if (n.children) {
          for (const child of n.children) {
            walk(child);
          }
        }
      } else {
        paths.push(n.path);
      }
    };
    walk(node);
    return paths;
  };

  // Helper: Get folder check state
  const getFolderCheckState = (node: TreeNode): 'checked' | 'indeterminate' | 'unchecked' => {
    if (!node.isFolder) {
      return 'unchecked';
    }
    const descendantPaths = getDescendantFilePaths(node);
    if (descendantPaths.length === 0) {
      return 'unchecked';
    }
    const checkedCount = descendantPaths.filter((p) => selectedFilePaths.has(p)).length;
    if (checkedCount === descendantPaths.length) {
      return 'checked';
    }
    if (checkedCount > 0) {
      return 'indeterminate';
    }
    return 'unchecked';
  };

  // Computed: all files selected state
  const allFilesSelected = useMemo(() => {
    const allFiles = getAllFilePaths(tree);
    if (allFiles.length === 0) {
      return false;
    }
    return allFiles.every((p) => selectedFilePaths.has(p));
  }, [tree, selectedFilePaths]);

  const someFilesSelected = useMemo(() => {
    return selectedFilePaths.size > 0 && !allFilesSelected;
  }, [selectedFilePaths.size, allFilesSelected]);

  const getCopyErrorMessage = (err: unknown) => {
    if (err instanceof DOMException) {
      if (err.name === 'NotAllowedError') {
        return 'クリップボードの権限がありません。ブラウザの設定をご確認ください。';
      }
      if (err.name === 'QuotaExceededError') {
        return 'ツリーが大きすぎてコピーできませんでした。';
      }
    }
    return err instanceof Error
      ? err.message
      : 'コピーできませんでした。ブラウザの設定をご確認ください。';
  };

  const handleCopyTree = async () => {
    try {
      setCopying(true);
      const response = await githubTreeSimplify({
        owner: repository.owner,
        repo: repository.repo,
        ref: repository.branch || undefined,
        recursive: true,
      });
      const text = JSON.stringify(response, null, 2);
      await navigator.clipboard.writeText(text);
      showToast('Copied');
    } catch (err) {
      showToast(getCopyErrorMessage(err), 'error');
    } finally {
      setCopying(false);
    }
  };

  const toggleFolder = (node: TreeNode) => {
    const next = new Set(expandedFolders);
    if (expandedFolders.has(node.path)) {
      next.delete(node.path);
    } else {
      next.add(node.path);
    }
    setExpandedFolders(next);
  };

  const handleItemClick = (e: MouseEvent<HTMLDivElement>, node: TreeNode) => {
    e.stopPropagation();
    if (node.isFolder) {
      toggleFolder(node);
      return;
    }
    onFileSelect(node.path);
  };

  const decodeBase64ToText = (value: string) => {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  };

  const isProbablyBinary = (value: string) => value.includes('\u0000');

  const resolveFileContent = (data: {
    content: string;
    encoding?: string;
  }) => {
    const content =
      data.encoding === 'base64' ? decodeBase64ToText(data.content) : data.content;
    if (isProbablyBinary(content)) {
      throw new Error('バイナリファイルはコピーできません。');
    }
    return content;
  };

  const copyFileContent = async (filePath: string) => {
    if (copyingPaths.has(filePath)) {
      return;
    }
    setCopyingPaths((prev) => new Set(prev).add(filePath));
    try {
      const data = await githubFileGet({
        owner: repository.owner,
        repo: repository.repo,
        path: filePath,
        ref: repository.branch,
        format: 'raw',
      });
      const content = resolveFileContent(data);
      await navigator.clipboard.writeText(content);
      showToast('ファイル内容をコピーしました');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy file');
      showToast(
        err instanceof Error ? err.message : 'ファイル内容のコピーに失敗しました',
        'error'
      );
    } finally {
      setCopyingPaths((prev) => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
    }
  };

  // Checkbox handlers
  const handleCheckboxChange = (node: TreeNode, checked: boolean) => {
    const filesToToggle = node.isFolder ? getDescendantFilePaths(node) : [node.path];

    setSelectedFilePaths((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const path of filesToToggle) {
          if (next.size >= MAX_SELECTED_FILES) {
            break;
          }
          next.add(path);
        }
      } else {
        for (const path of filesToToggle) {
          next.delete(path);
        }
      }
      return next;
    });
  };

  const handleSelectAllChange = () => {
    if (allFilesSelected || someFilesSelected) {
      setSelectedFilePaths(new Set()); // Clear all
    } else {
      const allFiles = getAllFilePaths(tree);
      setSelectedFilePaths(new Set(allFiles.slice(0, MAX_SELECTED_FILES)));
    }
  };

  // Parallel file fetching
  const fetchFilesInParallel = async (
    filePaths: string[],
    concurrencyLimit: number
  ): Promise<Array<{ path: string; content: string } | { path: string; error: string; isBinary?: boolean }>> => {
    const results: Array<{ path: string; content: string } | { path: string; error: string; isBinary?: boolean }> = [];
    let completed = 0;

    // Filter out binary files by extension first
    const nonBinaryPaths = filePaths.filter((path) => {
      if (isBinaryExtension(path)) {
        results.push({ path, error: 'Binary file', isBinary: true });
        completed += 1;
        setMergeProgress({ current: completed, total: filePaths.length });
        return false;
      }
      return true;
    });

    // Fetch files in batches
    for (let i = 0; i < nonBinaryPaths.length; i += concurrencyLimit) {
      const batch = nonBinaryPaths.slice(i, i + concurrencyLimit);
      const batchPromises = batch.map(async (path) => {
        try {
          const data = await githubFileGet({
            owner: repository.owner,
            repo: repository.repo,
            path,
            ref: repository.branch,
            format: 'raw',
          });

          // Check if binary based on encoding
          if (data.encoding === 'base64') {
            const content = decodeBase64ToText(data.content);
            if (isProbablyBinary(content)) {
              return { path, error: 'Binary file', isBinary: true };
            }
            return { path, content };
          }

          return { path, content: data.content };
        } catch (err) {
          return { path, error: err instanceof Error ? err.message : 'Failed to fetch' };
        } finally {
          completed += 1;
          setMergeProgress({ current: completed, total: filePaths.length });
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    return results;
  };

  // Generate merged markdown
  const generateMergedMarkdown = (
    files: Array<{ path: string; content: string }>,
    owner: string,
    repo: string,
    branch: string
  ): string => {
    // Sort by path
    const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path));

    let markdown = `# Merged Files\nRepository: ${owner}/${repo}\nBranch: ${branch}\n\n---\n\n`;

    for (const file of sorted) {
      const language = getLanguageFromPath(file.path);
      markdown += `${file.path}\n\`\`\`${language}\n${file.content}\n\`\`\`\n\n`;
    }

    return markdown;
  };

  // Merge handler
  const handleMerge = async () => {
    if (selectedFilePaths.size === 0) {
      showToast('ファイルが選択されていません', 'error');
      return;
    }

    setIsMerging(true);
    setMergeProgress({ current: 0, total: selectedFilePaths.size });

    try {
      const results = await fetchFilesInParallel(
        Array.from(selectedFilePaths),
        PARALLEL_FETCH_LIMIT
      );

      const successful = results.filter(
        (r): r is { path: string; content: string } => 'content' in r
      );
      const binaryCount = results.filter((r) => 'error' in r && r.isBinary).length;
      const failedCount = results.filter((r) => 'error' in r && !r.isBinary).length;

      if (successful.length === 0) {
        showToast('マージできるファイルがありませんでした', 'error');
        return;
      }

      const markdown = generateMergedMarkdown(
        successful,
        repository.owner,
        repository.repo,
        repository.branch
      );
      setMergedContent(markdown);
      setIsMergeModalOpen(true);

      if (binaryCount > 0 || failedCount > 0) {
        const skippedCount = binaryCount + failedCount;
        showToast(`${skippedCount}ファイルをスキップしました`, 'error');
      }
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'マージに失敗しました',
        'error'
      );
    } finally {
      setIsMerging(false);
    }
  };

  const TreeItem = ({ node, depth = 0 }: { node: TreeNode; depth?: number }) => {
    const isExpanded = expandedFolders.has(node.path);
    const indent = depth * 16;
    const isSelected = !node.isFolder && selectedFile === node.path;
    const backgroundColor = isSelected ? vscodeTheme.colors.selectedBackground : 'transparent';

    const folderCheckState = node.isFolder ? getFolderCheckState(node) : 'unchecked';
    const isChecked = node.isFolder
      ? folderCheckState === 'checked'
      : selectedFilePaths.has(node.path);
    const isDisabled =
      !selectedFilePaths.has(node.path) && selectedFilePaths.size >= MAX_SELECTED_FILES;

    return (
      <>
        <div
          onClick={(e) => handleItemClick(e, node)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            paddingLeft: `${indent + 8}px`,
            cursor: 'pointer',
            color: vscodeTheme.colors.textPrimary,
            fontSize: vscodeTheme.typography.fontSize,
            fontFamily: vscodeTheme.typography.fontFamily,
            backgroundColor,
            transition: 'background-color 0.1s',
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor =
                vscodeTheme.colors.hoverBackground;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <input
            type="checkbox"
            checked={isChecked}
            ref={(el) => {
              if (el && node.isFolder) {
                el.indeterminate = folderCheckState === 'indeterminate';
              }
            }}
            disabled={isDisabled}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(node, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              marginRight: '6px',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
          />
          {node.isFolder && (
            <span style={{ marginRight: '4px', fontSize: '10px' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          <span style={{ marginRight: '6px' }}>{node.isFolder ? '📁' : '📄'}</span>
          <span style={{ flex: 1 }}>{node.name}</span>
          {!node.isFolder && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void copyFileContent(node.path);
              }}
              disabled={copyingPaths.has(node.path)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: copyingPaths.has(node.path) ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textSecondary,
                fontSize: '12px',
              }}
              title={
                copyingPaths.has(node.path)
                  ? 'コピー中...'
                  : 'ファイル内容をコピー'
              }
            >
              {copyingPaths.has(node.path) ? '⏳' : '⧉'}
            </button>
          )}
        </div>
        {node.isFolder && isExpanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeItem key={child.path} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '10px', color: vscodeTheme.colors.textPrimary }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '10px', color: '#f48771' }}>Error: {error}</div>
    );
  }

  return (
    <div>
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${vscodeTheme.colors.border}`,
          backgroundColor: vscodeTheme.colors.headerBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            type="checkbox"
            checked={allFilesSelected}
            ref={(el) => {
              if (el) {
                el.indeterminate = someFilesSelected;
              }
            }}
            onChange={handleSelectAllChange}
            style={{ cursor: 'pointer' }}
            title={
              allFilesSelected
                ? '全選択解除'
                : someFilesSelected
                  ? '全選択'
                  : '全選択'
            }
          />
          <span
            style={{
              fontSize: '11px',
              color: vscodeTheme.colors.textSecondary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            EXPLORER
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isMerging ? (
            <div
              style={{
                fontSize: '11px',
                color: vscodeTheme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>
                {mergeProgress.current}/{mergeProgress.total}
              </span>
              <div
                style={{
                  width: '100px',
                  height: '4px',
                  backgroundColor: vscodeTheme.colors.border,
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(mergeProgress.current / mergeProgress.total) * 100}%`,
                    height: '100%',
                    backgroundColor: vscodeTheme.colors.textSecondary,
                    transition: 'width 0.2s',
                  }}
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleMerge}
              disabled={selectedFilePaths.size === 0}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: selectedFilePaths.size === 0 ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textSecondary,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: selectedFilePaths.size === 0 ? 0.5 : 1,
              }}
              title="選択ファイルをマージ"
            >
              <span aria-hidden="true">⧉</span>
              <span style={{ fontSize: '11px' }}>Merge</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleCopyTree}
            disabled={copying}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: copying ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textSecondary,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            title="ツリーJSONをコピー"
          >
            {copying ? (
              <span aria-label="copying" role="status">
                ⏳
              </span>
            ) : (
              <span aria-hidden="true">⧉</span>
            )}
            <span style={{ fontSize: '11px' }}>Copy</span>
          </button>
        </div>
      </div>
      {tree.map((node) => (
        <TreeItem key={node.path} node={node} />
      ))}

      {isMergeModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setIsMergeModalOpen(false)}
        >
          <div
            style={{
              width: '80vw',
              height: '80vh',
              backgroundColor: vscodeTheme.colors.editorBackground,
              border: `1px solid ${vscodeTheme.colors.border}`,
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${vscodeTheme.colors.border}`,
                backgroundColor: vscodeTheme.colors.headerBackground,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: vscodeTheme.colors.textPrimary,
                  fontFamily: vscodeTheme.typography.fontFamily,
                }}
              >
                Merged Files
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(mergedContent);
                      showToast('マージ内容をコピーしました');
                    } catch (err) {
                      showToast(getCopyErrorMessage(err), 'error');
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    cursor: 'pointer',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '12px',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                  }}
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => setIsMergeModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    cursor: 'pointer',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '12px',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* Body - Monaco Editor */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Editor
                height="100%"
                defaultLanguage="markdown"
                value={mergedContent}
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
